import React, { useState } from 'react';
import { Mail, ArrowUpRight, ExternalLink, Copy, Check, ShieldCheck } from 'lucide-react';
import { SUMMIT_CONFIG } from '../config/summitConfig';

const InstagramIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
  </svg>
);

export const ContactSection: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(SUMMIT_CONFIG.contactEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <section 
      id="contact" 
      className="relative py-28 sm:py-36 bg-gradient-to-b from-deepblack via-[#141414] to-deepblack overflow-hidden perspective-stage border-t border-white/5"
    >
      {/* Background Architectural Watermark */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02] flex items-center justify-center font-serif text-[26vw] font-bold text-beige select-none">
        COMM
      </div>

      <div className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32 relative z-10">
        
        {/* Section Pre-title */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-[1px] bg-bronze" />
          <span className="text-xs uppercase tracking-architectural text-bronze font-semibold">
            COMMUNICATIONS & SECRETARIAT
          </span>
          <div className="w-12 h-[1px] bg-bronze" />
        </div>

        {/* Section Heading */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-14 sm:mb-16 gap-6">
          <div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ivory">
              CONNECT WITH <br />
              <span className="text-beige font-light italic">FLS SECRETARIAT</span>
            </h2>
            <p className="mt-4 text-sm sm:text-base text-ivory/60 max-w-xl font-light leading-relaxed">
              Direct all institutional delegations, delegation queries, and formal correspondence to our organizing directorate through our verified official communication channels.
            </p>
          </div>

          <div className="flex items-center gap-3 text-xs tracking-widest uppercase text-beige/80 bg-charcoal-subtle border border-bronze/30 px-5 py-3 rounded-sm self-start lg:self-end">
            <ShieldCheck className="w-4 h-4 text-beige" />
            <span>DIRECT SECRETARIAT ACCESS</span>
          </div>
        </div>

        {/* 2 Main Communication Channels: Email & Instagram */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
          
          {/* Card 1: Official Email Channel */}
          <div className="marble-card rounded-sm p-7 sm:p-10 flex flex-col justify-between relative transition-all duration-300 border border-bronze/40 hover:border-beige/70 group shadow-xl">
            {/* Greek Key Header Trim */}
            <div className="w-full h-1.5 greek-key-border opacity-50 mb-6" />

            <div>
              {/* Badge & Channel Indicator */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <span className="text-[10px] font-mono font-bold tracking-widest text-bronze uppercase px-3 py-1 bg-white/5 border border-bronze/30 rounded-sm">
                  OFFICIAL DISPATCH
                </span>
                <span className="text-[10px] uppercase tracking-widest text-beige flex items-center gap-1.5 font-semibold">
                  <Mail className="w-3.5 h-3.5 text-bronze" />
                  EMAIL INBOX
                </span>
              </div>

              {/* Icon & Title */}
              <div className="flex items-center gap-4 mb-5">
                <div className="p-3.5 border border-bronze/40 rounded-full text-beige bg-deepblack group-hover:border-beige transition-colors shadow-inner">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl font-bold text-ivory tracking-wide">
                    OFFICIAL EMAIL
                  </h3>
                  <span className="text-[11px] text-ivory/50 uppercase tracking-widest block font-mono">
                    24/7 DIPLOMATIC CORRESPONDENCE
                  </span>
                </div>
              </div>

              {/* Email Address Display Box with Copy Button */}
              <div className="my-6 p-4 rounded-sm bg-black/60 border border-bronze/30 flex items-center justify-between gap-3">
                <span className="font-mono text-xs sm:text-sm text-beige select-all break-all font-semibold">
                  {SUMMIT_CONFIG.contactEmail}
                </span>
                <button
                  onClick={handleCopyEmail}
                  type="button"
                  title="Copy email address"
                  className="p-2 rounded border border-bronze/40 text-ivory/70 hover:text-beige hover:border-beige transition-colors shrink-0 bg-charcoal-subtle"
                  data-cursor-interactive
                >
                  {copied ? (
                    <span className="flex items-center gap-1 text-[10px] text-beige font-mono">
                      <Check className="w-3.5 h-3.5 text-green-400" />
                      COPIED
                    </span>
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>

              <p className="text-xs sm:text-sm text-ivory/70 font-light leading-relaxed mb-6">
                For school and college delegation allotments, executive inquiries, agenda queries, and official summit correspondence.
              </p>
            </div>

            {/* Direct Send Email Button */}
            <div className="pt-6 border-t border-bronze/30">
              <a
                href={`mailto:${SUMMIT_CONFIG.contactEmail}`}
                className="w-full py-4 px-6 bg-gradient-to-r from-beige-300 via-beige to-beige-400 text-deepblack text-xs sm:text-sm uppercase tracking-widest font-bold rounded-sm border border-beige hover:shadow-glow-bronze transition-all flex items-center justify-center gap-2 group/btn shadow-xl"
                data-cursor-interactive
                data-cursor-text="MAIL"
              >
                <span>SEND EMAIL TO SECRETARIAT</span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
              </a>
              <span className="text-[10px] text-ivory/40 text-center block mt-2.5">
                Opens your default email client with futureleaderssummitofficial@gmail.com
              </span>
            </div>
          </div>

          {/* Card 2: Official Instagram Channel */}
          <div className="marble-card rounded-sm p-7 sm:p-10 flex flex-col justify-between relative transition-all duration-300 border border-bronze/40 hover:border-beige/70 group shadow-xl">
            {/* Greek Key Header Trim */}
            <div className="w-full h-1.5 greek-key-border opacity-50 mb-6" />

            <div>
              {/* Badge & Channel Indicator */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <span className="text-[10px] font-mono font-bold tracking-widest text-bronze uppercase px-3 py-1 bg-white/5 border border-bronze/30 rounded-sm">
                  OFFICIAL BROADCAST
                </span>
                <span className="text-[10px] uppercase tracking-widest text-beige flex items-center gap-1.5 font-semibold">
                  <InstagramIcon className="w-3.5 h-3.5 text-bronze" />
                  INSTAGRAM CHANNEL
                </span>
              </div>

              {/* Icon & Title */}
              <div className="flex items-center gap-4 mb-5">
                <div className="p-3.5 border border-bronze/40 rounded-full text-beige bg-deepblack group-hover:border-beige transition-colors shadow-inner">
                  <InstagramIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-serif text-2xl sm:text-3xl font-bold text-ivory tracking-wide">
                    OFFICIAL INSTAGRAM
                  </h3>
                  <span className="text-[11px] text-ivory/50 uppercase tracking-widest block font-mono">
                    LIVE SUMMIT BROADCAST
                  </span>
                </div>
              </div>

              {/* Instagram Handle Display Box */}
              <div className="my-6 p-4 rounded-sm bg-black/60 border border-bronze/30 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs sm:text-base text-beige font-semibold">
                    @future.leaders.summit
                  </span>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-ivory/50 font-mono">
                  VERIFIED PROFILE
                </span>
              </div>

              <p className="text-xs sm:text-sm text-ivory/70 font-light leading-relaxed mb-6">
                Follow our official Instagram for urgent conference updates, executive board reveals, study guides, delegate preparation tips, and live summit coverage.
              </p>
            </div>

            {/* Direct Instagram Button */}
            <div className="pt-6 border-t border-bronze/30">
              <a
                href={SUMMIT_CONFIG.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 px-6 bg-gradient-to-r from-beige-300 via-beige to-beige-400 text-deepblack text-xs sm:text-sm uppercase tracking-widest font-bold rounded-sm border border-beige hover:shadow-glow-bronze transition-all flex items-center justify-center gap-2 group/btn shadow-xl"
                data-cursor-interactive
                data-cursor-text="INSTA"
              >
                <span>OPEN @FUTURE.LEADERS.SUMMIT</span>
                <ExternalLink className="w-4 h-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
              </a>
              <span className="text-[10px] text-ivory/40 text-center block mt-2.5">
                Opens https://www.instagram.com/future.leaders.summit/ in a new tab
              </span>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};
